// filepath: /Users/paulojustinchan/Downloads/workspace/PriveMD/achieve-admin-frontend/src/components/ProgressOverview/ProgressOverview.js
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Card,
    CardContent,
    Typography,
    Box,
    CardHeader,
    Divider,
    Grid2,
    FormControl,
    Select,
    MenuItem,
    IconButton,
} from "@mui/material";
import moment from "moment";
import {
    HEALTH_STARTING_TYPE,
    HEALTH_TYPE,
    HEALTH_UNIT,
    PERMISSIONS,
} from "../../helper/constant";
import DatePickerComponent from "../DatePicker/DatePicker";
import dayjs from "dayjs";
import {
    computeBMI,
    DATE_FORMAT,
    formatStepCount,
    hasAccess,
    getNumberSign,
    printLog,
    computeBodyFatChange,
    onRequestError,
    httpRequest,
    formatDateTime,
    formatBodyFatPercentage,
} from "../../helper/global";
import ProgressOverviewItem from "./ProgressOverviewItem";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { hideLoader, showLoader } from "../../reducers/loaderSlice";
import { setPatientHealthData } from "../../reducers/patientSlice";

export default function ProgressOverview({
    theme,
    dispatch,
    navigate,
    user,
    permissionConfig,
    patient,
    buildInputForm,
    handleOnSave,
    weightDateRange = 1,
    handleWeightDateRangeChange,
}) {
    const [startingBMI, setStartingBMI] = useState(0);

    const TIME_RANGES = [
        { label: "All Time", value: "all" },
        { label: "Avg 7 Days", value: 7 },
        { label: "Avg 14 Days", value: 14 },
        { label: "Avg 30 Days", value: 30 },
        { label: "Avg 1 Year", value: 365 },
    ];

    const handleRangeChange = (event) => {
        const value = event.target.value;

        handleWeightDateRangeChange(value);
    };

    const calculateWeightLoss = (start, current) => {
        if (!start || !current) {
            return { weightLoss: 0, weightLossPercent: 0 };
        }
        const weightLoss = current - start;
        const weightLossPercent = ((weightLoss / start) * 100).toFixed(2);
        return {
            weightLoss: `${getNumberSign(weightLoss)}${weightLoss.toFixed(2)}`,
            weightLossPercent: `${getNumberSign(
                weightLoss,
            )}${weightLossPercent}%`,
        };
    };

    const calculatBMILoss = (start, current) => {
        if (!start || !current) {
            return { bmiLoss: 0, bmiLossPercent: 0 };
        }
        const bmiLoss = current - start;
        const bmiLossPercent = ((bmiLoss / start) * 100).toFixed(2);
        return {
            bmiLoss: `${getNumberSign(bmiLoss)}${bmiLoss.toFixed(2)} ${
                HEALTH_UNIT[HEALTH_TYPE.BMI]
            }`,
            bmiLossPercent: `${getNumberSign(
                bmiLossPercent,
            )}${bmiLossPercent}%`,
        };
    };

    const calculatBodyFatLoss = (start, current) => {
        if (!start || !current) {
            return {
                bodyFatPercentageLoss: 0,
                bodyFatPercentageLossPercent: 0,
            };
        }
        const bodyFatPercentageLoss = current - start;
        const bodyFatPercentageLossPercent = (
            (bodyFatPercentageLoss / start) *
            100
        ).toFixed(2);
        return {
            bodyFatPercentageLoss: `${getNumberSign(
                bodyFatPercentageLoss,
            )}${bodyFatPercentageLoss.toFixed(2)}`,
            bodyFatPercentageLossPercent: `${getNumberSign(
                bodyFatPercentageLoss,
            )}${bodyFatPercentageLossPercent}%`,
        };
    };

    const calculatLeanBodyMassLoss = (start, current) => {
        if (!start || !current) {
            return {
                leanBodyMassLoss: 0,
                leanBodyMassLossPercent: 0,
            };
        }
        const leanBodyMassLoss = current - start;
        const leanBodyMassLossPercent = (
            (leanBodyMassLoss / start) *
            100
        ).toFixed(2);
        return {
            leanBodyMassLoss: `${getNumberSign(
                leanBodyMassLoss,
            )}${leanBodyMassLoss.toFixed(2)}`,
            leanBodyMassLossPercent: `${getNumberSign(
                leanBodyMassLoss,
            )}${leanBodyMassLossPercent}%`,
        };
    };

    const calculateWeeksInTreatment = (startDate) => {
        if (!startDate) {
            return "-";
        }

        const start = moment(startDate);
        const now = moment();

        const days = now.diff(start, "day");
        if (days <= 7) {
            return `${days} day(s)`;
        }
        return `${now.diff(start, "week")} week(s)`;
    };

    const { weightLoss, weightLossPercent } = calculateWeightLoss(
        weightDateRange === "all"
            ? patient.healthData.startingWeight
            : patient.bodyMassPrior,
        patient.bodyMass ?? 0,
    );

    const [bmiLoss, setBMILoss] = useState(0);
    const [bmiLossPercent, setBMILossPercent] = useState("0%");

    const weeksInTreatment = calculateWeeksInTreatment(
        patient.healthData.startingWeightDate ?? null,
    );

    const startingWeightRef = useRef(null);
    const [status_starting_weigth, set_status_starting_weight] =
        useState(false);

    const startingBodyFatPercentageRef = useRef(null);
    const [status_starting_body_fat, set_status_starting_body_fat] =
        useState(false);

    const startingLeanBodyMassRef = useRef(null);
    const [status_starting_lean_body_mass, set_status_starting_lean_body_mass] =
        useState(false);

    const [rateOfChange, setRateOfChange] = useState(0);
    const rateOfChangeUnit = "lbs / week";

    // const handleTreatmentStartDate = (newValue) => {
    //     const date = newValue.format(DATE_FORMAT.defaultDate);

    //     handleOnSave(HEALTH_STARTING_TYPE.treatmentStartDate, date);
    // };

    const handleWeightManagementStartDate = (newValue) => {
        const date = newValue.format(DATE_FORMAT.defaultDate);

        handleOnSave(HEALTH_STARTING_TYPE.startingWeightDate, date);
    };

    useEffect(() => {
        if (!patient || !patient.healthData) {
            return;
        }

        const bmi = computeBMI(
            patient.healthData.startingWeight,
            patient.healthData.height,
        );

        const { bmiLoss, bmiLossPercent } = calculatBMILoss(
            weightDateRange === "all" ? bmi : patient.bmiPrior,
            patient.bmi ?? 0,
        );

        setStartingBMI(bmi);
        setBMILoss(bmiLoss);
        setBMILossPercent(bmiLossPercent);
    }, [
        patient.healthData.startingWeight,
        patient.healthData.height,
        patient.bmi,
        patient.bmiPrior,
        weightDateRange,
    ]);

    const [startingWeight, setStartingWeight] = useState(
        patient.healthData.startingWeight,
    );

    const [startingBodyFatPercentage, setStartingBodyFatPercentage] = useState(
        patient.healthData.startingBodyFatPercentage,
    );

    const [bodyFatPercentageLoss, setBodyFatPercentageLoss] = useState(0);
    const [bodyFatPercentageLossPercent, setBodyFatPercentageLossPercent] =
        useState("0%");

    const [startingLeanBodyMass, setStartingLeanBodyMass] = useState(0);
    const [leanBodyMassLoss, setLeanBodyMassLoss] = useState(0);
    const [leanBodyMassLossPercent, setLeanBodyMassLossPercent] =
        useState("0%");

    useEffect(() => {
        if (!patient || !patient.healthData) {
            return;
        }

        setStartingWeight(patient.healthData.startingWeight);
        setStartingBodyFatPercentage(
            patient.healthData.startingBodyFatPercentage,
        );

        const { bodyFatPercentageLoss, bodyFatPercentageLossPercent } =
            calculatBodyFatLoss(
                weightDateRange === "all"
                    ? patient.healthData.startingBodyFatPercentage
                    : patient.bodyFatPercentagePrior,
                patient.bodyFatPercentage ?? 0,
            );

        setBodyFatPercentageLoss(bodyFatPercentageLoss);
        setBodyFatPercentageLossPercent(bodyFatPercentageLossPercent);

        // Compute startingLeanBodyMass using startingWeight and startingBodyFatPercentage
        let startingLeanBodyMassValue =
            parseFloat(patient.healthData.startingLeanBodyMass) ?? 0;
        if (
            patient.healthData.startingWeight &&
            patient.healthData.startingBodyFatPercentage !== undefined &&
            patient.healthData.startingBodyFatPercentage !== null &&
            !startingLeanBodyMassValue
        ) {
            startingLeanBodyMassValue =
                patient.healthData.startingWeight *
                (1 - patient.healthData.startingBodyFatPercentage / 100);
        }
        setStartingLeanBodyMass(startingLeanBodyMassValue);
        const { leanBodyMassLoss, leanBodyMassLossPercent } =
            calculatLeanBodyMassLoss(
                weightDateRange === "all"
                    ? startingLeanBodyMassValue
                    : patient.leanBodyMassPrior,
                patient.leanBodyMass ?? 0,
            );
        setLeanBodyMassLoss(leanBodyMassLoss);
        setLeanBodyMassLossPercent(leanBodyMassLossPercent);
    }, [
        patient.healthData.startingWeight,
        patient.healthData.startingBodyFatPercentage,
        patient.healthData.startingLeanBodyMass,
        patient.bodyFatPercentage,
        patient.leanBodyMass,
        weightDateRange,
    ]);

    const handleEstimateBodyFat = useCallback(async () => {
        if (!user || !permissionConfig) {
            return;
        }

        if (!hasAccess(user, permissionConfig, "patient", PERMISSIONS.write)) {
            return;
        }

        try {
            dispatch(showLoader({ message: "Estimating Body Fat %..." }));
            const result = await httpRequest(
                `ai/request/estimate-body-fat-percentage`,
                {
                    patientId: patient.id,
                },
                "POST",
                (error) => {
                    dispatch(hideLoader());
                    onRequestError(dispatch, navigate, {
                        title: error.title,
                        message: error.message,
                    });
                },
            );

            dispatch(hideLoader());

            if (result === false) {
                return;
            }

            handleOnSave(HEALTH_TYPE.BODY_FAT_PERCENTAGE, result.payload * 1);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [patient]);

    useEffect(() => {
        if (!weightLoss || weightLoss == 0) {
            setRateOfChange(null);
            return;
        }

        let rateOfChangeValue = null;

        switch (weightDateRange) {
            case "all":
                if (
                    !patient.healthData.startingWeightDate ||
                    !patient.bodyMassDate
                ) {
                    setRateOfChange(null);
                    return;
                }

                //compute # of days from startingWeightDate to patient.bodyMassDate
                const startDate = dayjs(patient.healthData.startingWeightDate);
                const endDate = dayjs(patient.bodyMassDate);
                const diffDays = endDate.diff(startDate, "day");

                rateOfChangeValue = ((weightLoss / diffDays) * 7).toFixed(2); // weekly rate of change

                break;

            default:
                const weightDateRangeInt = parseInt(weightDateRange);

                rateOfChangeValue = (
                    (weightLoss / weightDateRangeInt) *
                    7
                ).toFixed(2); // weekly rate of change

                break;
        }
        setRateOfChange(rateOfChangeValue);
    }, [
        patient.healthData.startingWeight,
        patient.healthData.startingWeightDate,
        patient.bodyMassDate,
        weightDateRange,
        weightLoss,
    ]);

    return (
        <Card>
            <CardHeader
                title="Weight Loss Progress Overview"
                action={
                    <Box display={"flex"} alignItems={"center"} gap={1}>
                        <Typography variant="body1" color="textSecondary">
                            Date Range
                        </Typography>
                        <Select
                            value={weightDateRange}
                            onChange={handleRangeChange}
                            displayEmpty
                            size="small"
                            inputProps={{
                                "aria-label": "Select Time Range",
                            }}
                        >
                            {TIME_RANGES.map((range) => (
                                <MenuItem
                                    key={`time-range-${range.value}`}
                                    value={range.value}
                                >
                                    {range.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>
                }
            />
            <Divider />
            <CardContent>
                <Grid2 container spacing={2}>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Weight Management Date
                            </Typography>
                            <FormControl
                                sx={{ width: "180px" }}
                                variant="standard"
                            >
                                <DatePickerComponent
                                    isDisabled={
                                        !hasAccess(
                                            user,
                                            permissionConfig,
                                            "patient",
                                            PERMISSIONS.write,
                                        )
                                    }
                                    onChange={handleWeightManagementStartDate}
                                    defaultValue={
                                        patient.healthData.startingWeightDate
                                            ? dayjs(
                                                  patient.healthData
                                                      .startingWeightDate,
                                              )
                                            : null
                                    }
                                />
                            </FormControl>
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Weeks in Treatment
                            </Typography>
                            <Typography variant="h6">
                                {weeksInTreatment}
                            </Typography>
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Rate of Change"
                            value={rateOfChange}
                            unit={rateOfChange === "-" ? "" : rateOfChangeUnit}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Steps
                            </Typography>
                            <Typography variant="h6">
                                {patient[HEALTH_TYPE.STEP_COUNT]
                                    ? formatStepCount(
                                          patient[HEALTH_TYPE.STEP_COUNT],
                                      )
                                    : "-"}
                            </Typography>
                        </Box>
                    </Grid2>

                    {/* FIRST DATA ENTRY IF AVAILABLE */}
                    {patient.firstEntryData &&
                        patient.firstEntryData.length > 0 && (
                            <>
                                <Grid2
                                    container
                                    spacing={2}
                                    size={{ xs: 12 }}
                                    sx={{
                                        backgroundColor: (theme) =>
                                            theme.palette.mode === "dark"
                                                ? "rgba(144, 202, 249, 0.08)"
                                                : "rgba(25, 118, 210, 0.08)",
                                        p: 1,
                                        borderRadius: "4px",
                                    }}
                                >
                                    <Grid2 size={{ xs: 12, md: 12 }}>
                                        <Typography
                                            variant="body1"
                                            sx={{ fontWeight: "bold" }}
                                        >
                                            First Entry Data
                                        </Typography>
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <ProgressOverviewItem
                                            title="Date"
                                            value={formatDateTime(
                                                patient.firstEntryData[0]
                                                    .date ?? null,
                                                "MM/DD/YYYY",
                                                false,
                                            )}
                                        />
                                    </Grid2>
                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <ProgressOverviewItem
                                            title="Weight"
                                            value={
                                                patient.firstEntryData[0]
                                                    .bodyMass ?? ""
                                            }
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <ProgressOverviewItem
                                            title="Body Fat %"
                                            value={formatBodyFatPercentage(
                                                patient.firstEntryData[0]
                                                    .bodyFatPercentage,
                                            )}
                                            unit={"%"}
                                        />
                                    </Grid2>

                                    <Grid2 size={{ xs: 12, md: 3 }}>
                                        <ProgressOverviewItem
                                            title="Lean Body Mass"
                                            value={
                                                patient.firstEntryData[0]
                                                    .leanBodyMass ?? ""
                                            }
                                            unit={HEALTH_UNIT.bodyMass}
                                        />
                                    </Grid2>
                                </Grid2>
                            </>
                        )}
                    {/* FIRST DATA ENTRY IF AVAILABLE */}

                    {/* HEADER */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            Starting
                        </Typography>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            Prior
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            Current
                        </Typography>
                    </Grid2>

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                            Change
                        </Typography>
                    </Grid2>
                    {/* HEADER */}

                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Weight
                            </Typography>
                            {buildInputForm(
                                startingWeightRef,
                                "number",
                                HEALTH_TYPE.BODY_MASS,
                                startingWeight,
                                status_starting_weigth,
                                (e) => {
                                    setStartingWeight(e.target.value);
                                    set_status_starting_weight(true);
                                },
                                () => set_status_starting_weight(false),
                            )}
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Weight"
                            value={
                                weightDateRange === "all"
                                    ? startingWeight
                                    : patient.bodyMassPrior
                            }
                            unit={HEALTH_UNIT.bodyMass}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Weight"
                            value={patient.bodyMass}
                            unit={HEALTH_UNIT.bodyMass}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Weight"
                            value={weightLoss}
                            unit={HEALTH_UNIT.bodyMass}
                            subText={`(${weightLossPercent})`}
                        />
                    </Grid2>

                    {/* BMI */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                BMI
                            </Typography>
                            <Typography variant="h6">{startingBMI}</Typography>
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="BMI"
                            value={
                                weightDateRange === "all"
                                    ? startingBMI
                                    : patient.bmiPrior
                            }
                            unit={HEALTH_UNIT.BMI}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="BMI"
                            value={patient.bmi}
                            unit={HEALTH_UNIT.BMI}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="BMI"
                            value={bmiLoss}
                            unit={HEALTH_UNIT.BMI}
                        />
                    </Grid2>

                    {/* BODY FAT PERCENTAGE */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <Box sx={{ mb: 2 }}>
                            <Typography variant="body1" color="textSecondary">
                                Body Fat %
                                <IconButton
                                    aria-label="estimate"
                                    onClick={(event) => {
                                        event.stopPropagation();

                                        handleEstimateBodyFat();
                                    }}
                                    title="AI Estimate Body Fat %"
                                    sx={{ p: 1, m: 0 }}
                                >
                                    <AutoAwesomeIcon
                                        sx={{
                                            color: "#ffa726",
                                            fontSize: "1rem",
                                        }}
                                    />
                                </IconButton>
                            </Typography>
                            {buildInputForm(
                                startingBodyFatPercentageRef,
                                "number",
                                HEALTH_TYPE.BODY_FAT_PERCENTAGE,
                                startingBodyFatPercentage,
                                status_starting_body_fat,
                                (e) => {
                                    setStartingBodyFatPercentage(
                                        e.target.value,
                                    );
                                    set_status_starting_body_fat(true);
                                },
                                () => set_status_starting_body_fat(false),
                            )}
                        </Box>
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Body Fat %"
                            value={
                                weightDateRange === "all"
                                    ? startingBodyFatPercentage
                                    : patient[
                                          `${HEALTH_TYPE.BODY_FAT_PERCENTAGE}Prior`
                                      ]
                            }
                            unit="%"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Body Fat %"
                            value={patient[HEALTH_TYPE.BODY_FAT_PERCENTAGE]}
                            unit="%"
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Body Fat"
                            value={computeBodyFatChange(
                                weightLoss,
                                leanBodyMassLoss,
                            )}
                            unit={HEALTH_UNIT.bodyMass}
                            // subText={`(${Math.abs(
                            //     (computeBodyFatChange(
                            //         weightLoss,
                            //         leanBodyMassLoss,
                            //     ) /
                            //         weightLoss) *
                            //         100,
                            // ).toFixed(0)}%)`}
                        />
                    </Grid2>

                    {/* LEAN BODY MASS */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Lean Body Mass"
                            value={startingLeanBodyMass.toFixed(2)}
                            unit={HEALTH_UNIT.leanBodyMass}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Lean Body Mass"
                            value={
                                weightDateRange === "all"
                                    ? startingLeanBodyMass.toFixed(2)
                                    : patient[
                                          `${HEALTH_TYPE.LEAN_BODY_MASS}Prior`
                                      ]
                            }
                            unit={HEALTH_UNIT.leanBodyMass}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Lean Body Mass"
                            value={patient[HEALTH_TYPE.LEAN_BODY_MASS]}
                            unit={HEALTH_UNIT.leanBodyMass}
                        />
                    </Grid2>
                    <Grid2 size={{ xs: 12, md: 3 }}>
                        <ProgressOverviewItem
                            title="Lean Body Mass"
                            value={leanBodyMassLoss}
                            unit={HEALTH_UNIT.leanBodyMass}
                            // subText={`(${Math.abs(
                            //     (leanBodyMassLoss / weightLoss) * 100,
                            // ).toFixed(0)}%)`}
                        />
                    </Grid2>
                </Grid2>
            </CardContent>
        </Card>
    );
}
